'use client';

import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import Link from 'next/link';
import { ArrowLeft, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ApplyJobForm from '../apply-job-form';

export default function AgentPosition() {
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const perks = [
    'Generous 80% Commission on every deal you close',
    'Access to top listing platforms (Bayut, Dubizzle, Property Finder)',
    'Property Monitor for data analysis and market insights',
    'Dedicated administrative and marketing support',
  ];

  const requirements = [
    'Hold a valid RERA license and have a minimum of 1 year experience in UAE real estate',
    'Fluent in English. Additional languages are a plus',
    'Go-getter attitude and dedication to exceeding expectations',
    'Valid UAE driving license. Diploma or higher education is a plus',
  ];

  const benefits = [
    'Become part of a dynamic and successful team',
    'Enjoy a work-life balance that allows you to thrive',
    'Earn top dollar for your hard work',
  ];

  return (
    <div className='bg-white dark:bg-gray-950 pt-28 pb-20'>
      <div className='container mx-auto px-4'>
        <Link
          href='/careers'
          className='inline-flex items-center text-[#fe2a53] dark:text-[#38f5ef] hover:underline mb-8'
        >
          <ArrowLeft className='w-4 h-4 mr-2' />
          Back to Careers
        </Link>

        <div className='max-w-5xl mx-auto'>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Header */}
            <div className='text-center mb-12'>
              <h1 className='text-3xl md:text-4xl font-bold text-[#fe2a53] dark:text-[#38f5ef] mb-4'>
                Real Estate Agent
              </h1>
              <h2 className='sr-only'>X-Space Real Estate Broker | Real Estate Agent - JOB DESCRIPTION</h2>
              <h3 className='text-2xl font-semibold text-[#fe2a53] dark:text-[#38f5ef]'>
                JOB DESCRIPTION
              </h3>
              <p className='text-xl font-bold text-[#fe2a53] dark:text-[#38f5ef] mt-2'>
                (1 year's Experience MUST)
              </p>
            </div>

            {/* Intro */}
            <div className='bg-white dark:bg-gray-800 rounded-3xl shadow-lg p-6 md:p-10 mb-12'>
              <h3 className='text-2xl font-bold text-gray-900 dark:text-white mb-4 text-center'>
                Unleash Your Potential at X-Space Real Estate
              </h3>
              <div className='space-y-2 text-gray-700 dark:text-gray-300 text-lg text-center'>
                <p>- Want to Earn 80% Commission?</p>
                <p>
                  - Do you crave a supportive work environment that rewards your
                  hustle?
                </p>
                <p>- Then look no further than X-Space Real Estate!</p>
              </div>
            </div>

            {/* Perks & Compensation */}
            <div className='grid gap-8 md:grid-cols-2 mb-12'>
              <div className='bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6'>
                <h3 className='text-xl md:text-2xl font-bold text-[#fe2a53] dark:text-[#38f5ef] mb-4'>
                  Exceptional Compensation & Support
                </h3>
                <ul className='space-y-2 text-gray-700 dark:text-gray-300'>
                  {perks.map((perk, i) => (
                    <li key={i} className='flex items-start'>
                      <CheckCircle className='w-5 h-5 text-[#fe2a53] dark:text-[#38f5ef] mt-1 mr-2 flex-shrink-0' />
                      {perk}
                    </li>
                  ))}
                </ul>
              </div>

              <div className='bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6'>
                <h3 className='text-xl md:text-2xl font-bold text-[#fe2a53] dark:text-[#38f5ef] mb-4'>
                  Requirements
                </h3>
                <ul className='space-y-2 text-gray-700 dark:text-gray-300'>
                  {requirements.map((req, i) => (
                    <li key={i} className='flex items-start'>
                      <CheckCircle className='w-5 h-5 text-[#fe2a53] dark:text-[#38f5ef] mt-1 mr-2 flex-shrink-0' />
                      {req}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Benefits */}
            <div className='bg-gradient-to-r from-[#fe2a53]/30 to-[#fe2a53]/10 dark:from-[#38f5ef]/30 dark:to-[#38f5ef]/10 rounded-3xl shadow-lg p-6 md:p-10 mb-12'>
              <h3 className='text-xl md:text-2xl font-bold text-[#fe2a53] dark:text-[#38f5ef] mb-4 text-center'>
                Why Join X-Space Real Estate?
              </h3>
              <ul className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 text-gray-700 dark:text-gray-300'>
                {benefits.map((benefit, i) => (
                  <li key={i} className='flex items-center'>
                    <CheckCircle className='w-5 h-5 text-[#fe2a53] dark:text-[#38f5ef] mr-2 flex-shrink-0' />
                    {benefit}
                  </li>
                ))}
              </ul>
            </div>

            {/* Closing */}
            <div className='text-center mb-12'>
              <h3 className='text-xl font-bold text-[#fe2a53] dark:text-[#38f5ef] mb-4'>
                We can't wait to meet you!
              </h3>
              <p className='text-gray-700 dark:text-gray-300'>
                We're also known as Good Reputation Real Estate, so don't be
                surprised if you hear that name too.
              </p>
            </div>

            {/* Apply Button */}
            <div className='text-center'>
              <Button
                onClick={() => setShowForm(true)}
                className='bg-gradient-to-r bg-primarycolor dark:bg-secondarycolor hover:bg-secondarycolor dark:hover:bg-primarycolor text-white hover:text-black dark:hover:text-white px-8 py-6 text-lg rounded-full'
              >
                Apply for this Position
              </Button>
            </div>
          </motion.div>
        </div>
      </div>

      {showForm && (
        <ApplyJobForm
          onClose={() => setShowForm(false)}
          jobTitle='Real Estate Agent'
        />
      )}
    </div>
  );
}
